import { useEffect, useRef } from 'react';
import { useExperiments } from '@alltrails/experiments';
import { useAppDispatch, useAppSelector } from '../redux';
import { updateBaseStyleId } from '../redux/reducer';
import type { BaseStyleId } from '../types/Styles';

type UseBaseStyleOverridesOptions = {
  /**
   * When provided, dispatches this style id once on mount and skips all other overrides.
   * Used by location pages to stay on alltrailsOutdoorsV2 while everything else defaults to V3.
   * @see https://alltrails.atlassian.net/browse/DISCO-25086
   */
  initialBaseStyleId?: BaseStyleId;
};

/**
 * Centralises base-style overrides that run after the Redux store is initialised.
 *
 * Location pages pin the style to alltrailsOutdoorsV2 via `initialBaseStyleId`.
 */
export default function useBaseStyleOverrides(options?: UseBaseStyleOverridesOptions) {
  const { initialBaseStyleId } = options ?? {};
  const dispatch = useAppDispatch();
  const baseStyleId = useAppSelector(state => state.map.baseStyleId);
  const experiments = useExperiments();
  const isDynamicOsmPinsEnabled = experiments['web-disco-rm-dynamic-osm-pins']?.value === 'on';

  const hasAppliedInitialOverride = useRef(false);

  useEffect(() => {
    if (initialBaseStyleId && !hasAppliedInitialOverride.current) {
      hasAppliedInitialOverride.current = true;
      if (baseStyleId !== initialBaseStyleId) {
        dispatch(updateBaseStyleId(initialBaseStyleId));
      }
      return;
    }

    // If no initial base style id is provided, default to the appropriate base style based on the dynamic OSM pins experiment
    if (isDynamicOsmPinsEnabled && baseStyleId === 'alltrailsOutdoorsV3') {
      dispatch(updateBaseStyleId('alltrailsOutdoorsV2Wrapper'));
    } else if (!isDynamicOsmPinsEnabled && baseStyleId === 'alltrailsOutdoorsV2Wrapper') {
      dispatch(updateBaseStyleId('alltrailsOutdoorsV3'));
    }
  }, [baseStyleId, dispatch, initialBaseStyleId, isDynamicOsmPinsEnabled]);
}
